
import { useEffect, useState, useRef } from 'react';

import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { getDataWithId, facilityUpdateWithId, facilityCreate, facilityDeleteByIds, facilityActiveWithId } from 'api/endpoints/facility';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Input from 'components/Input';
import TitleInput from 'components/TitleInput';
import { toastError, toastSuccess } from 'lib/toast';

import PhoneInput from './PhoneInput';

// ─── helpers ────────────────────────────────────────────────────────────────
const PHONE_REGEX = /^\d{2}-\d{4}-\d{4}$/; // 00-0000-0000

const CreateOrDetailFacility = ({ reloadData, onClose }) => {
    const dispatch = useDispatch();
    const { modalFacility } = useSelector((state) => state.facility);

    const isEdit = modalFacility?.isEdit;

    const [isFetching, setIsFetching] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const isCallingApi = useRef(false);

    // ── react-hook-form setup ──
    const {
        register,
        handleSubmit,
        control,
        reset,
        watch,
        formState: { errors, isSubmitting },
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            name: '',           // 施設名称
            code: '',           // 施設コード  (auto-generated if empty)
            address: '',        // 住所
            tel_no: '',         // 電話番号
            manager_name: '',   // 施設長名
            is_active: true,     // 有効フラグ
        },
    });

    // ── populate form when editing — fetch fresh data by id ──────────────────
    useEffect(() => {
        if (!isEdit || !modalFacility?.id) return;

        const fetchAndPopulate = async () => {
            isCallingApi.current = true;
            setIsFetching(true);

            try {
                await getDataWithId({ id: modalFacility.id })
                    .then((response) => {
                        const data = response.data ?? {};
                        reset({
                            name: data.name ?? '',
                            code: data.code ?? '',
                            address: data.address ?? '',
                            tel_no: data.tel_no ?? '',
                            manager_name: data.manager_name ?? '',
                            is_active: data.is_active ?? true,
                        });
                        setIsFetching(false);
                    })
                    .catch(() => {
                        setIsFetching(false);
                    });
            } catch {
                setIsFetching(false);
            } finally {
                isCallingApi.current = false;
            }
        };

        fetchAndPopulate();
    }, [isEdit, modalFacility?.id, reset]);


    // ── submit handler ────────────────────────────────────────────────────────
    const onSubmit = async (values) => {
        isCallingApi.current = true;
        setIsSubmit(true);

        try {
            const apiFn = isEdit
                ? () => facilityUpdateWithId(modalFacility.id, values)
                : () => facilityCreate(values);

            const response = await apiFn();
            const data = response.data;

            if (data?.code !== '403' && data?.code !== '500') {
                toastSuccess(response?.message);
                reloadData?.();
            } else {
                toastError(response?.message || '施設の更新に失敗しました。');
            }
        } catch {
            toastError('施設の更新に失敗しました。');
        } finally {
            isCallingApi.current = false;
            setIsSubmit(false);
            onClose?.();
            reloadData?.();
        }
    };

    // ── update active status ──────────────────────────────────────────────────
    const handleUpdateStatus = async () => {
        if (isEdit && !isSubmit) {
            const isActive = watch('is_active');
            try {
                const apiFn = isActive
                    ? () => facilityActiveWithId(modalFacility.id)
                    : () => facilityDeleteByIds([modalFacility.id]);

                const response = await apiFn();
                const data = response.data;

                if (data?.code !== '403' && data?.code !== '500') {
                    toastSuccess(response?.message || `施設が${isActive ? '有効' : '無効'}になりました。`);
                    reloadData?.();
                } else {
                    toastError(response?.message || '施設の更新に失敗しました。');
                }
            } catch {
                toastError('施設の更新に失敗しました。');
            } finally {
                reloadData?.();
            }
        }
    };

    // ── backdrop click ────────────────────────────────────────────────────────
    const clickBackdrop = (e) => {
        if (e.target.classList.contains('backdrop')) onClose?.();
    };

    // ── render modal ─────────────────────────────────────────────────────────
    return ReactDOM.createPortal(
        <div
            id="modal"
            className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50"
        >
            {/* backdrop */}
            <div onClick={clickBackdrop} className="fixed inset-0 backdrop" />

            {/* modal card */}
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="bg-white rounded-sm shadow-lg relative w-[767px] max-h-[96vh] overflow-y-auto z-10"
                noValidate
            >
                {/* ── header ── */}
                <div className="flex items-center justify-between px-6 py-4">
                    <p className="font-bold">{isEdit ? '施設の編集' : '施設の登録'}</p>
                </div>

                <div className="p-3 relative">
                    <div className="absolute top-0 left-0 w-full border-t border-gray-200 h-px" />

                    <div className="w-full px-3 grid grid-cols-1">
                        {/* ── 施設名称 (required, max 255, unique) ── */}
                        <div>
                            <TitleInput text="施設名称" required />
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: '施設名称は必須です。',
                                    pattern: {
                                        message: 'この項目は入力必須です。',
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        id="name"
                                        type="text"
                                        placeholder="施設名称"
                                        className="w-full"
                                        aria-invalid={errors.name ? 'true' : 'false'}
                                        invalid={errors.name}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        ref={field.ref}
                                        maxLength={255}
                                    />
                                )}
                            />
                            <div className="h-2">
                                {errors.name && (
                                    <p className="text-sm text-danger-229 max-w-full">
                                        <ErrorMessage>{errors.name.message}</ErrorMessage>
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* ── 施設コード (optional, auto-generated) ── */}
                        <div>
                            <TitleInput text="施設コード" />
                            <Controller
                                name="code"
                                control={control}
                                rules={{

                                    pattern: {
                                        // Includes digits, letters, and hyphens, but no spaces.
                                        value: /^[a-zA-Z0-9-]*$/,
                                        message: '施設コードは英数字とハイフンのみ使用できます。',
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        id="code"
                                        type="text"
                                        placeholder="施設コード"
                                        className="w-full"
                                        aria-invalid={errors.code ? 'true' : 'false'}
                                        invalid={errors.code}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        ref={field.ref}
                                        maxLength={500}
                                    />
                                )}
                            />
                            <div className="h-2">{errors.code && (
                                <p className="text-sm text-danger-229 max-w-full">
                                    <ErrorMessage>{errors.code.message}</ErrorMessage>
                                </p>
                            )}</div>

                        </div>

                        {/* ── 住所 (optional, max 500) ── */}
                        <div>
                            <TitleInput text="住所" />
                            <Controller
                                name="address"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        id="address"
                                        type="text"
                                        placeholder="住所"
                                        className="w-full"
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        ref={field.ref}
                                        maxLength={500}
                                    />
                                )}
                            />
                            <div className="h-2">{errors.address && (
                                <p className="text-sm text-danger-229 max-w-full">
                                    <ErrorMessage>{errors.address.message}</ErrorMessage>
                                </p>
                            )}</div>
                        </div>

                        {/* ── 電話番号 (required, format 00-0000-0000) ── */}
                        <div>
                            <TitleInput text="電話番号" required />
                            <Controller
                                name="tel_no"
                                control={control}
                                rules={{
                                    required: '電話番号は必須です。',
                                    pattern: {
                                        value: PHONE_REGEX,
                                        message: '電話番号の形式が正しくありません（例: 03-1234-5678）',
                                    },
                                }}
                                render={({ field }) => (
                                    <PhoneInput
                                        id="phone_number"
                                        placeholder="00-0000-0000"
                                        className="w-full"
                                        aria-invalid={errors.tel_no ? 'true' : 'false'}
                                        invalid={errors.tel_no}
                                        {...field}
                                    />
                                )}
                            />
                            <div className="h-2">
                                {errors.tel_no && (
                                    <p className="text-sm text-danger-229 max-w-full">
                                        <ErrorMessage>{errors.tel_no.message}</ErrorMessage>
                                    </p>
                                )}
                            </div>

                        </div>

                        {/* ── 施設長名 (required, max 100) ── */}
                        <div>
                            <TitleInput text="施設長名" required />
                            <Controller
                                name="manager_name"
                                control={control}
                                rules={{
                                    required: '施設長名は必須です。',
                                }}
                                render={({ field }) => (
                                    <Input
                                        id="manager_name"
                                        type="text"
                                        placeholder="施設長名"
                                        className="w-full"
                                        aria-invalid={errors.manager_name ? 'true' : 'false'}
                                        invalid={errors.manager_name}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        ref={field.ref}
                                        maxLength={100}
                                    />
                                )}
                            />
                            <div className="h-2"> {errors.manager_name && (
                                <p className="text-sm text-danger-229 max-w-full"><ErrorMessage>{errors.manager_name.message}</ErrorMessage></p>
                            )} </div>

                        </div>
                        {/* ── 有効フラグ default: true ── */}
                        <div>
                            <TitleInput text="有効フラグ" required />
                            <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => (
                                    // use toggle for boolean value
                                    <label className="inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={field.value}
                                            onChange={(e) => {
                                                field.onChange(e.target.checked);
                                                handleUpdateStatus();
                                            }}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            ref={field.ref}
                                            disabled={!isEdit}
                                        />
                                        <span className="
                                            relative w-12 h-6 rounded-full
                                            bg-gray-200 peer-checked:bg-primary-346
                                            transition-colors duration-300
                                            after:content-[''] after:absolute
                                            after:top-0.5 after:left-0.5
                                            after:w-5 after:h-5 after:rounded-full
                                            after:bg-white after:shadow
                                            after:transition-transform after:duration-300
                                            peer-checked:after:translate-x-6
                                        "/>
                                        <div className={`ml-2 ${field.value ? 'flex flex-col' : ''} gap-1 text-sm`}>
                                            <p className="text-gray-700 font-semibold">{field.value ? '有効' : '無効'}</p>
                                            {field.value && <p className="text-gray-500">ONにすると施設がユーザーに表示されます</p>}
                                        </div>
                                    </label>
                                )}
                            />
                            <div className="h-2"> </div>
                        </div>
                    </div>
                </div>

                {/* ── footer ── */}
                <div className="h-16 border-t border-gray-200 flex items-center justify-end gap-2 py-4 px-6">
                    <Button
                        type="button"
                        className="h-8 text-sm w-25.5"
                        text="キャンセル"
                        color="outline"
                        onClick={onClose}
                    />
                    <Button
                        type="submit"
                        className="h-8 text-sm w-44.5"
                        text={isEdit ? '変更を保存する' : '施設を登録する'}
                        color="primary"
                        disabled={isSubmitting}
                    />
                </div>
            </form>
        </div>,
        document.body
    );
};

export default CreateOrDetailFacility;